<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast', `toast-${toast.type}`]"
          @mouseenter="pauseTimer(toast.id)"
          @mouseleave="resumeTimer(toast.id)"
        >
          <!-- Icon -->
          <div class="toast-icon">
            <svg v-if="toast.type === 'success'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else-if="toast.type === 'error'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <svg v-else-if="toast.type === 'warning'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <!-- Content -->
          <div class="toast-content">
            <p v-if="toast.title" class="toast-title">{{ toast.title }}</p>
            <p class="toast-message">{{ toast.message }}</p>
          </div>

          <!-- Close Button -->
          <button
            @click="removeToast(toast.id)"
            class="toast-close"
            type="button"
            aria-label="Close"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { inject } from 'vue';
import type { ToastState } from '@/utils/toast';

const toastState = inject<ToastState>('toastState');
if (!toastState) {
  throw new Error('ToastNotification must be used with toast provider');
}

const { toasts, removeToast, pauseTimer, resumeTimer } = toastState;
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 420px;
  width: 100%;
  pointer-events: none;
}

@media (max-width: 640px) {
  .toast-container {
    left: 1rem;
    right: 1rem;
    max-width: none;
  }
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  pointer-events: auto;
  background: white;
  border: 1px solid;
  min-width: 300px;
}

.toast-success {
  border-color: rgb(187, 247, 208);
  background: rgb(240, 253, 244);
}

.toast-success .toast-icon {
  color: rgb(22, 101, 52);
}

.toast-success .toast-title {
  color: rgb(22, 101, 52);
}

.toast-success .toast-message {
  color: rgb(21, 128, 61);
}

.toast-error {
  border-color: rgb(254, 202, 202);
  background: rgb(254, 242, 242);
}

.toast-error .toast-icon {
  color: rgb(127, 29, 29);
}

.toast-error .toast-title {
  color: rgb(127, 29, 29);
}

.toast-error .toast-message {
  color: rgb(185, 28, 28);
}

.toast-warning {
  border-color: rgb(253, 230, 138);
  background: rgb(254, 252, 232);
}

.toast-warning .toast-icon {
  color: rgb(120, 53, 15);
}

.toast-warning .toast-title {
  color: rgb(120, 53, 15);
}

.toast-warning .toast-message {
  color: rgb(161, 98, 7);
}

.toast-info {
  border-color: rgb(191, 219, 254);
  background: rgb(239, 246, 255);
}

.toast-info .toast-icon {
  color: rgb(30, 64, 175);
}

.toast-info .toast-title {
  color: rgb(30, 64, 175);
}

.toast-info .toast-message {
  color: rgb(37, 99, 235);
}

.toast-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.toast-message {
  font-size: 0.875rem;
  line-height: 1.5;
}

.toast-close {
  flex-shrink: 0;
  padding: 0.125rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
  color: currentColor;
  opacity: 0.7;
}

.toast-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
}

/* Transition animations */
.toast-enter-active {
  animation: toast-in 0.3s ease-out;
}

.toast-leave-active {
  animation: toast-out 0.2s ease-in;
}

@keyframes toast-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes toast-out {
  from {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateX(100%) scale(0.95);
    opacity: 0;
  }
}
</style>

